'use client';

import { useState, useSyncExternalStore } from 'react';
import { Flag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import { Switch } from '@/components/ui/switch';
import { useFeatureFlags } from '@/lib/providers/feature-flags-provider';
import { cn } from '@/lib/utils';

const KNOWN_FLAGS = [
  'estate-account',
  'global-documents',
  'grant-concierge',
  'onboarding',
  'pms-integrations',
  'safe-custody',
  'trial-gate-opt-out',
  'trial-gate-opt-in',
  'zapier-integration'
] as const;

/** No-op subscribe for the hydration-safe `mounted` read below. */
const subscribeNoop = () => () => {};

export function DevFeatureFlagToggle() {
  const [open, setOpen] = useState(false);
  const { features, overrides, setFeatureOverride } = useFeatureFlags();
  // Overrides are read from localStorage, so they exist on the client but not
  // during SSR. Deferring anything that renders differently because of them to
  // after mount keeps the first client render byte-identical to the server's —
  // otherwise the trigger's className diverges and React logs a hydration
  // mismatch on every page load with an override set.
  const mounted = useSyncExternalStore(
    subscribeNoop,
    () => true,
    () => false
  );

  if (process.env.NODE_ENV !== 'development') {
    return null;
  }

  const hasActiveOverrides = mounted && Object.keys(overrides).length > 0;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          className={cn(
            // Half-size and semi-transparent at rest, full size on hover /
            // focus / while open. It floats over the bottom-right corner, which
            // is where drawer and dialog footer CTAs sit — at full size it
            // covered them. Dev-only, so this is purely local ergonomics.
            'group fixed right-4 bottom-16 z-50 h-7 w-7 rounded-full opacity-60 shadow-md transition-all',
            'hover:h-14 hover:w-14 hover:opacity-100 hover:shadow-xl',
            'focus-visible:h-14 focus-visible:w-14 focus-visible:opacity-100',
            'data-[state=open]:h-14 data-[state=open]:w-14 data-[state=open]:opacity-100',
            hasActiveOverrides && 'opacity-100 ring-2 ring-amber-400'
          )}
          size="icon"
          title="Toggle Feature Flags"
        >
          <Flag className="h-3.5 w-3.5 transition-all group-hover:h-6 group-hover:w-6" />
        </Button>
      </PopoverTrigger>
      <PopoverContent side="top" align="end" className="w-72">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold">Feature Flags</h4>
            {hasActiveOverrides && (
              <button
                onClick={() => {
                  for (const flag of Object.keys(overrides)) {
                    setFeatureOverride(flag, null);
                  }
                }}
                className="text-muted-foreground hover:text-foreground text-xs underline"
              >
                Reset all
              </button>
            )}
          </div>
          <div className="space-y-2">
            {KNOWN_FLAGS.map((flag) => {
              const isActive = features.includes(flag);
              const hasOverride = flag in overrides;

              return (
                <div
                  key={flag}
                  className="flex items-center justify-between gap-2"
                >
                  <div className="flex items-center gap-2 overflow-hidden">
                    <div
                      className={cn(
                        'h-2 w-2 shrink-0 rounded-full',
                        isActive ? 'bg-green-500' : 'bg-gray-300'
                      )}
                    />
                    <span
                      className={cn(
                        'truncate text-sm',
                        hasOverride && 'font-medium text-amber-600'
                      )}
                    >
                      {flag}
                    </span>
                  </div>
                  <Switch
                    checked={isActive}
                    onCheckedChange={(checked) =>
                      setFeatureOverride(flag, checked)
                    }
                  />
                </div>
              );
            })}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
